import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Package,
  Layers,
  ShieldCheck,
  Building2,
  Layers3,
  Wrench,
  Grid3X3,
  Search,
  X,
} from "lucide-react";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  CATEGORIES,
  MANUFACTURERS,
  PRODUCTS,
  PRODUCT_IMAGES,
  getCategoryLabel,
} from "@/data/products";

const CATEGORY_ICONS = {
  epdm: Layers,
  tpo: ShieldCheck,
  pvc: Building2,
  fleece: Layers3,
  insulation: Package,
  accessories: Wrench,
  modbit: Grid3X3,
};

export const ProductVisual = ({ product }) => {
  const Icon = CATEGORY_ICONS[product.category] || Package;
  return (
    <div
      data-testid={`product-visual-${product.slug}`}
      className="relative flex h-44 w-full flex-col items-center justify-center bg-gradient-to-br from-navy-900 to-navy-700 px-4 text-center"
    >
      <div className="rounded-full bg-steel-500/25 p-3">
        <Icon className="h-6 w-6 text-steel-300" />
      </div>
      <span className="font-display mt-2 text-sm font-bold text-white">
        {product.name}
      </span>
      <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-steel-300">
        {getCategoryLabel(product.category)}
      </span>
      <span className="mt-2 rounded-full border border-steel-300/40 px-2.5 py-0.5 text-[10px] font-semibold text-steel-300">
        Official product photo coming soon
      </span>
    </div>
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [cat, setCat] = useState("all");
  const [mfr, setMfr] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(`cat-${hash}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = PRODUCTS.filter(
    (p) =>
      (cat === "all" || p.category === cat) &&
      (mfr === "All" || p.manufacturer === mfr) &&
      (!q ||
        [p.name, p.manufacturer, p.material, p.applications, p.blurb, getCategoryLabel(p.category)]
          .join(" ")
          .toLowerCase()
          .includes(q))
  );

  const groups = CATEGORIES.map((c) => ({
    ...c,
    items: filtered.filter((p) => p.category === c.slug),
  })).filter((g) => g.items.length > 0);

  const requestQuote = (name) =>
    navigate(`/?interest=${encodeURIComponent(name)}`);

  return (
    <div data-testid="products-page" className="min-h-screen bg-gray-100">
      <SiteHeader />

      <section className="bg-navy-900 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-steel-300">
            Quadraciti Product Catalog
          </p>
          <h1
            data-testid="products-page-headline"
            className="font-display mt-3 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl"
          >
            All Roofing Products
          </h1>
          <p className="mt-4 max-w-2xl text-base text-navy-100">
            {PRODUCTS.length} commercial roofing products from Carlisle SynTec,
            GAF, Johns Manville, Elevate, Sika Sarnafil, and Versico —
            membranes, insulation, cover boards, and accessories.
          </p>
          <div className="mt-6 flex items-center gap-3 text-sm text-navy-200">
            <Package className="h-4 w-4" />
            <span data-testid="products-count">
              Showing {filtered.length} of {PRODUCTS.length} products
            </span>
          </div>
        </div>
      </section>

      <section className="border-b border-gray-200 bg-white py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="relative mb-6 max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="product-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${PRODUCTS.length} products — e.g. EPDM, Sure-Weld, fasteners...`}
              className="pl-9 pr-9"
            />
            {query && (
              <button
                data-testid="product-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">
            Category
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="filter-cat-all"
              onClick={() => setCat("all")}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                cat === "all"
                  ? "bg-navy-800 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                data-testid={`filter-cat-${c.slug}`}
                onClick={() => setCat(c.slug)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  cat === c.slug
                    ? "bg-navy-800 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <p className="mb-3 mt-6 text-xs font-bold uppercase tracking-widest text-gray-500">
            Manufacturer
          </p>
          <div className="flex flex-wrap gap-2">
            {["All", ...MANUFACTURERS].map((m) => (
              <button
                key={m}
                data-testid={`filter-mfr-${m.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setMfr(m)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  mfr === m
                    ? "bg-steel-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {groups.length === 0 && (
          <p data-testid="products-empty" className="py-16 text-center text-gray-500">
            No products match these filters.
          </p>
        )}
        {groups.map((group) => (
          <section
            key={group.slug}
            id={`cat-${group.slug}`}
            data-testid={`category-block-${group.slug}`}
            className="mb-14 scroll-mt-32"
          >
            <div className="mb-6 flex items-baseline justify-between">
              <h2 className="font-display text-2xl font-extrabold text-navy-900">
                {group.label}
              </h2>
              <span className="text-sm font-semibold text-gray-500">
                {group.items.length} product{group.items.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {group.items.map((p) => (
                <Card
                  key={p.slug}
                  data-testid={`product-item-${p.slug}`}
                  className="flex flex-col overflow-hidden border-gray-200 bg-white transition-shadow hover:shadow-xl"
                >
                  <div className="relative">
                    <Link data-testid={`product-link-${p.slug}`} to={`/products/${p.slug}`} className="block">
                      <ProductVisual product={p} />
                      {PRODUCT_IMAGES[p.slug] && (
                        <img
                          data-testid={`product-image-${p.slug}`}
                          src={PRODUCT_IMAGES[p.slug]}
                          alt={p.name}
                          className="absolute inset-0 h-full w-full bg-white object-cover"
                        />
                      )}
                    </Link>
                    <span className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-navy-900 shadow">
                      {p.manufacturer}
                    </span>
                  </div>
                  <CardContent className="flex flex-1 flex-col p-5">
                    <Link to={`/products/${p.slug}`}>
                      <h3 className="font-display text-lg font-bold text-navy-900 transition-colors hover:text-steel-500">
                        {p.name}
                      </h3>
                    </Link>
                    <p className="mt-2 text-sm text-gray-600">
                      <span className="font-semibold text-navy-800">Material:</span>{" "}
                      {p.material}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      <span className="font-semibold text-navy-800">Applications:</span>{" "}
                      {p.applications}
                    </p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-500">
                      {p.blurb}
                    </p>
                    <Button
                      data-testid={`quote-btn-${p.slug}`}
                      variant="outline"
                      onClick={() => requestQuote(p.name)}
                      className="mt-4 border-navy-800 font-semibold text-navy-800 hover:bg-navy-800 hover:text-white"
                    >
                      Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ))}
      </main>

      <section className="bg-navy-900 py-14">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white sm:text-3xl">
              Need help choosing a system?
            </h2>
            <p className="mt-2 max-w-xl text-base text-navy-100">
              Tell us about your roof and we'll recommend the right membrane,
              insulation, and accessories for the job.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid="products-page-quote-button"
              size="lg"
              onClick={() => requestQuote("General")}
              className="bg-white px-8 font-bold text-navy-900 hover:bg-navy-100"
            >
              Request a Quote
            </Button>
            <a href={PHONE_HREF}>
              <Button
                data-testid="products-page-call-button"
                size="lg"
                variant="outline"
                className="w-full border-2 border-white bg-transparent px-8 font-bold text-white hover:bg-white hover:text-navy-900 sm:w-auto"
              >
                <Phone className="mr-2 h-4 w-4" /> {PHONE_DISPLAY}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ProductsPage;
